/* Copyright (c) 2024 MOCA Systems, Inc. All rights reserved. */

import {
	CommonPredicateOp,
	DatePredicateOp,
	DropDownPredicateOp,
	ITPFilter,
	NumericPredicateOp,
	TextPredicateOp,
	TpFilterPredicateOp,
} from '../app-data/models/TPFilterPredicates';

type comparisonValueType = string | number | boolean | Date | string[];

/**
 * Evaluates the comparison between the ticket value and the filter value.
 *
 * @param comparisonType - TP Filter Predicate Operator to be used for comparison.
 * @param compareValue1 - Value1 to be compared.
 * @param compareValue2 - Value2 to be compared.
 * @returns True if the ticket value passes the filter criteria, otherwise false.
 */
export function isComparisonMatch(
	comparisonType: TpFilterPredicateOp,
	compareValue1: comparisonValueType,
	compareValue2: comparisonValueType
): boolean {
	switch (comparisonType) {
		case CommonPredicateOp.EqualTo:
		case TextPredicateOp.ExactlyMatches:
		case NumericPredicateOp.EqualTo:
		case DatePredicateOp.EqualTo:
			return compareValue1 === compareValue2;

		case NumericPredicateOp.NotEqualTo:
		case CommonPredicateOp.NotEqualTo:
		case DatePredicateOp.NotEqualTo:
			return compareValue1 !== compareValue2;

		case NumericPredicateOp.LessThan:
		case DatePredicateOp.LessThan:
			return compareValue1 < compareValue2;

		case NumericPredicateOp.GreaterThan:
		case DatePredicateOp.GreaterThan:
			return compareValue1 > compareValue2;

		case NumericPredicateOp.LessThanOrEqualTo:
		case DatePredicateOp.LessThanOrEqualTo:
			return compareValue1 <= compareValue2;

		case NumericPredicateOp.GreaterThanOrEqualTo:
		case DatePredicateOp.GreaterThanOrEqualTo:
			return compareValue1 >= compareValue2;

		case CommonPredicateOp.NotSet:
		case NumericPredicateOp.NotSet:
		case TextPredicateOp.NotSet:
		case DropDownPredicateOp.NotSet:
			return compareValue1 === null || compareValue1 === undefined;

		case CommonPredicateOp.AnyValue:
		case NumericPredicateOp.AnyValue:
		case TextPredicateOp.AnyValue:
		case DropDownPredicateOp.AnyValue:
			return compareValue1 != null && compareValue1 != undefined;

		case TextPredicateOp.Contains:
		case DropDownPredicateOp.IsOneOf:
			if (
				(typeof compareValue1 == 'string' || Array.isArray(compareValue1)) &&
				typeof compareValue2 == 'string'
			) {
				return compareValue1.includes(compareValue2);
			}
			return false;

		default:
			throw new Error(`Unknown comparison type: ${comparisonType}`);
	}
}

/**
 * Checks if a filter value matches the given criteria.
 *
 * @param valueToEvaluate - The value to evaluate if it matches the criteria.
 * @param filterFieldCriteria - The criteria to match against.
 * @returns A boolean indicating whether the value matches the criteria.
 */
export function doesValueMatchCriteria(
	valueToEvaluate: comparisonValueType,
	filterFieldCriteria: ITPFilter
): boolean {
	const { value, operator } = filterFieldCriteria;
	// If filter criteria value is not an array, and valueToEvaluate is an array,
	// check if any of the valueToEvaluate values pass the filter criteria.
	if (!Array.isArray(value) && Array.isArray(valueToEvaluate)) {
		return valueToEvaluate.some((val) => isComparisonMatch(operator, val, value));
	}
	// If filter criteria value is an array,
	// check if any of the filter values pass the filter criteria.
	if (Array.isArray(value) && !Array.isArray(valueToEvaluate)) {
		return value.some((val) => isComparisonMatch(operator, valueToEvaluate, val));
	}

	// If both values are arrays, check if any of the filter values pass the filter criteria.
	if (Array.isArray(value) && Array.isArray(valueToEvaluate)) {
		return valueToEvaluate.some((val) => {
			return value.some((val2) => isComparisonMatch(operator, val, val2));
		});
	}

	// if not, perform a direct comparison between valueToEvaluate and filterFieldCriteria value.
	return isComparisonMatch(operator, valueToEvaluate, value);
}

//cheesey little thing to flatten the combined enums into their string reps for easier comparison
// don't export
function filterValueNotRequired(key: string) {
	return key === CommonPredicateOp.AnyValue || key === CommonPredicateOp.NotSet;
}

// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6606
// - decide if this should check for more, like "is number when greater than"
/**
 * check if a filter is a comparison mode that requires a value
 */
export function isFilterValid(filter: ITPFilter) {
	const operator = filter.operator;
	return filterValueNotRequired(operator) || (filter.value !== null && filter.value !== undefined);
}
// END PLACEHOLDER REPLACE AS PART OF TP-6606
